// src/pages/Steps/ChatbotCreationSteps.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Stepper,
    Step,
    StepLabel,
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    useTheme,
} from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import BoltIcon from '@mui/icons-material/Bolt';

import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';

import CreateChatbotName from '../ChatbotSettings/CreateChatbotName';
import AvatarSettings from '../AvatarSettings';
import ListAudioLanguage from '../AudioLanguage/ListAudioLanguage';
import Prompt from '../ChatbotSettings/Prompt';
import ChatbotTrainingInfo from '../ChatbotSettings/ChatbotTrainingInfo';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
    success: '#059669',
};

const allSteps = [
    { label: 'Chatbot Name', slug: 'chatbot-name' },
    { label: 'Set Avatar', slug: 'set-avatar' },
    { label: 'Audio Language', slug: 'set-audio-language' },
    { label: 'Chatbot Settings', slug: 'set-chatbot-setting' },
    { label: 'Training Info', slug: 'chatbot-training-info' },
];

const determineVisibleSteps = (subscription) => {
    const isFreemiumPlans = subscription?.plan?.name === 'freemium' || subscription?.plan?.name === 'individual_freemium';
    if (isFreemiumPlans) {
        return allSteps.filter(step => step.slug !== 'set-audio-language');
    }
    return allSteps;
};

export default function ChatbotCreationSteps({ onSetupComplete }) {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { refreshChatbots } = useChatbots();

    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);
    const [pendingChatbot, setPendingChatbot] = useState(null);

    const chatbotNameRef = useRef(null);
    const avatarSettingsRef = useRef(null);
    const promptRef = useRef(null);
    const audioLanguageRef = useRef(null);

    useEffect(() => {
        document.title = 'Chatbot Setup | KlonIT';

        const userStr = localStorage.getItem("user");
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                const role = user?.active_team?.user_role_in_team || "";
                if (role.toLowerCase() !== "admin") {
                    navigate("/chatbots", { replace: true });
                }
            } catch (err) {
                navigate("/chatbots", { replace: true });
            }
        } else {
            navigate("/chatbots", { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        const initialize = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const userStr = localStorage.getItem('user');
                const userData = userStr ? JSON.parse(userStr) : {};

                const visibleSteps = determineVisibleSteps(userData.active_team?.subscription);
                setSteps(visibleSteps);

                const response = await axiosInstance.get(`/chatbots/user/pending-process-bot`);
                const botData = response.data.data;

                if (botData.chatbot) {
                    setPendingChatbot(botData.chatbot);
                    const currentStepIndex = visibleSteps.findIndex(step => step.slug === botData.chatbot.current_process_step);
                    setActiveStep(currentStepIndex >= 0 ? currentStepIndex : 0);
                } else {
                    setPendingChatbot(null);
                    setActiveStep(0);
                }
            } catch (err) {
                console.error('Error initializing chatbot setup:', err);
                setError("Could not load chatbot configuration.");
            } finally {
                setIsLoading(false);
            }
        };

        initialize();
    }, [navigate]);

    const handleStepCompletion = useCallback(async (data = {}) => {
        setIsProcessing(false);
        setError(null);

        try {
            const response = await axiosInstance.get(`/chatbots/user/pending-process-bot`);
            const botData = response.data.data;

            if (botData.chatbot) {
                setPendingChatbot(botData.chatbot);
                const nextStepIndex = steps.findIndex(step => step.slug === botData.chatbot.current_process_step);
                if (nextStepIndex !== -1) {
                    setActiveStep(nextStepIndex);
                } else {
                    setActiveStep(prevStep => Math.min(prevStep + 1, steps.length - 1));
                }
            } else {
                setPendingChatbot(null);
                setActiveStep(0);
            }
        } catch (err) {
            console.error('Error in step completion:', err);
            setError("Could not proceed to the next step.");
        }
    }, [steps, navigate, refreshChatbots]);

    const handleStepError = useCallback((errorMessage) => {
        setIsProcessing(false);
        setError(errorMessage || "An error occurred in this step.");
    }, []);

    const handleNext = async () => {
        setError(null);
        setIsProcessing(true);

        if (activeStep < 0 || activeStep >= steps.length) {
            handleStepError("Invalid step sequence.");
            return;
        }

        const currentStepSlug = steps[activeStep].slug;

        try {
            let stepResult = false;

            switch (currentStepSlug) {
                case 'chatbot-name':
                    if (chatbotNameRef.current?.triggerSave) {
                        stepResult = await chatbotNameRef.current.triggerSave();
                    }
                    break;
                case 'set-avatar':
                    if (avatarSettingsRef.current?.triggerSave) {
                        stepResult = await avatarSettingsRef.current.triggerSave();
                    }
                    break;
                case 'set-audio-language':
                    if (audioLanguageRef.current?.checkStepCompletion) {
                        stepResult = audioLanguageRef.current.checkStepCompletion();
                    } else {
                        stepResult = true;
                    }
                    break;
                case 'set-chatbot-setting':
                    if (promptRef.current?.triggerSave) {
                        stepResult = await promptRef.current.triggerSave();
                    }
                    break;
                case 'chatbot-training-info':
                    stepResult = true;
                    break;
                default:
                    stepResult = true;
                    break;
            }

            if (stepResult && pendingChatbot) {
                const isFinalStep = activeStep >= steps.length - 1;
                const nextStepSlug = isFinalStep ? 'completed' : steps[activeStep + 1].slug;

                await axiosInstance.put(`/chatbots/${pendingChatbot.id}`, {
                    current_process_step: nextStepSlug,
                    is_process_completed: isFinalStep,
                });

                if (isFinalStep) {
                    refreshChatbots();
                    navigate(`/chatbot/${pendingChatbot.chatbot_identity}/website`);
                    return;
                }

                await handleStepCompletion();
            }
        } catch (e) {
            console.error('Error in handleNext:', e);
            handleStepError(e.message || "Failed to process step.");
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    const getStepContent = (stepIndex) => {
        if (isLoading) {
            return (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                    <CircularProgress size={36} sx={{ color: colors.accent }} />
                </Box>
            );
        }

        const currentSlug = steps[stepIndex]?.slug;

        switch (currentSlug) {
            case 'chatbot-name':
                return <CreateChatbotName ref={chatbotNameRef} onComplete={handleStepCompletion} onError={handleStepError} />;
            case 'set-avatar':
                return <AvatarSettings ref={avatarSettingsRef} chatbotId={pendingChatbot?.chatbot_identity} isSetupMode={true} onComplete={handleStepCompletion} onError={handleStepError} />;
            case 'set-audio-language':
                return <ListAudioLanguage ref={audioLanguageRef} isSetupMode={true} chatbotId={pendingChatbot?.chatbot_identity} onSetupComplete={handleStepCompletion} onSetupError={handleStepError} />;
            case 'set-chatbot-setting':
                return <Prompt ref={promptRef} isSetupMode={true} chatbotId={pendingChatbot?.chatbot_identity} onComplete={handleStepCompletion} onError={handleStepError} />;
            case 'chatbot-training-info':
                return <ChatbotTrainingInfo />;
            default:
                return <Typography sx={{ fontFamily, color: colors.textSecondary }}>Loading Step Content...</Typography>;
        }
    };

    if (isLoading) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                gap: 2,
            }}>
                <CircularProgress size={40} sx={{ color: colors.accent }} />
                <Typography sx={{ fontFamily, color: colors.textSecondary, fontSize: '0.9rem' }}>
                    Loading setup...
                </Typography>
            </Box>
        );
    }

    if (error && !steps.length) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                gap: 2,
            }}>
                <Alert severity="error" sx={{ fontFamily, borderRadius: '12px' }}>{error}</Alert>
                <Button onClick={() => navigate('/chatbots')} sx={{ fontFamily, textTransform: 'none' }}>
                    Return to My Chatbots
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ fontFamily, maxWidth: '900px', width: '100%', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                <Box sx={{
                    width: { xs: 44, sm: 56 },
                    height: { xs: 44, sm: 56 },
                    borderRadius: { xs: '12px', sm: '16px' },
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: { xs: 1.5, sm: 2 },
                    boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
                }}>
                    <SmartToyOutlinedIcon sx={{ color: '#fff', fontSize: { xs: 22, sm: 28 } }} />
                </Box>
                <Typography sx={{
                    fontFamily,
                    fontSize: { xs: '1.15rem', sm: '1.5rem' },
                    fontWeight: 700,
                    color: colors.textPrimary,
                    letterSpacing: '-0.02em',
                }}>
                    Set Up Your Chatbot
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                    <BoltIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: colors.accent }} />
                    <Typography sx={{ fontFamily, fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: colors.textTertiary }}>
                        Step {activeStep + 1} of {steps.length}
                    </Typography>
                </Box>
            </Box>

            {/* Stepper */}
            <Box sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(12px)',
                borderRadius: { xs: '10px', sm: '16px' },
                p: { xs: 1.5, sm: 3 },
                mb: { xs: 2, sm: 3 },
                border: '1px solid rgba(0,0,0,0.06)',
            }}>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel={!isMobile}
                    orientation={isMobile ? 'vertical' : 'horizontal'}
                    sx={{
                        '& .MuiStepLabel-label': {
                            fontFamily,
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            fontWeight: 500,
                        },
                        '& .MuiStepLabel-label.Mui-active': {
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                        },
                        '& .MuiStepLabel-label.Mui-completed': {
                            fontWeight: 600,
                            color: colors.success,
                        },
                        '& .MuiStepIcon-root': {
                            fontSize: { xs: '1.2rem', sm: '1.5rem' },
                        },
                        '& .MuiStepIcon-root.Mui-active': {
                            color: theme.palette.primary.main,
                        },
                        '& .MuiStepIcon-root.Mui-completed': {
                            color: colors.success,
                        },
                        '& .MuiStepConnector-line': {
                            borderColor: 'rgba(0,0,0,0.08)',
                        },
                        ...(isMobile && {
                            '& .MuiStepConnector-root': {
                                ml: '10px',
                            },
                            '& .MuiStepConnector-line': {
                                minHeight: 16,
                            },
                        }),
                    }}
                >
                    {steps.map((step, index) => (
                        <Step key={step.slug} completed={index < activeStep}>
                            <StepLabel
                                StepIconComponent={index < activeStep ? () => (
                                    <Box sx={{
                                        width: { xs: 20, sm: 24 },
                                        height: { xs: 20, sm: 24 },
                                        borderRadius: '50%',
                                        bgcolor: colors.success,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <CheckCircleIcon sx={{ color: '#fff', fontSize: { xs: 14, sm: 16 } }} />
                                    </Box>
                                ) : undefined}
                            >
                                {step.label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: { xs: 1.5, sm: 2 }, fontFamily, borderRadius: { xs: '8px', sm: '12px' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {error}
                </Alert>
            )}

            {/* Step Content */}
            <Box sx={{
                bgcolor: 'rgba(255,255,255,0.75)',
                backdropFilter: 'blur(12px)',
                borderRadius: { xs: '10px', sm: '16px' },
                p: { xs: 1.5, sm: 3 },
                border: '1px solid rgba(0,0,0,0.06)',
                minHeight: { xs: '250px', sm: '350px' },
            }}>
                {getStepContent(activeStep)}
            </Box>

            {/* Navigation */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: { xs: 2, sm: 3 },
                px: { xs: 0, sm: 1 },
                gap: 1,
            }}>
                <Button
                    disabled={activeStep === 0 || isProcessing || (activeStep === 1 && pendingChatbot)}
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }} />}
                    sx={{
                        fontFamily,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        color: colors.textSecondary,
                        borderRadius: { xs: '10px', sm: '12px' },
                        px: { xs: 1.5, sm: 3 },
                        py: { xs: 0.75, sm: 1 },
                        bgcolor: 'rgba(255,255,255,0.7)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        '&:disabled': { opacity: 0.4 },
                        minWidth: { xs: 'auto', sm: 'auto' },
                    }}
                >
                    Back
                </Button>

                <Button
                    onClick={handleNext}
                    disabled={isProcessing}
                    endIcon={isProcessing
                        ? <CircularProgress size={16} color="inherit" />
                        : <ArrowForwardIcon sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }} />
                    }
                    sx={{
                        fontFamily,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        color: '#fff',
                        borderRadius: { xs: '10px', sm: '12px' },
                        px: { xs: 2, sm: 4 },
                        py: { xs: 0.75, sm: 1.25 },
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                            boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                            transform: 'translateY(-1px)',
                        },
                        '&:disabled': { opacity: 0.5, transform: 'none' },
                        transition: 'all 0.2s ease',
                    }}
                >
                    {isProcessing
                        ? "Processing..."
                        : activeStep === steps.length - 1
                            ? (isMobile ? "Finish Setup" : "Finish & Go to Dashboard")
                            : "Save & Continue"
                    }
                </Button>
            </Box>
        </Box>
    );
}
